import { useState } from "react";

import { css } from "@compiled/react";
import { IntlShape, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Row } from "@/libraries/wpl/foundations/src";

import TemplateCard, { TemplateCardProps } from "./template-card";
import { BagIcon } from "./template-icons/bag-icon";
import { BriefCaseIcon } from "./template-icons/briefcase-icon";
import { BullHornIcon } from "./template-icons/bullhorn-icon";
import { CogsIcon } from "./template-icons/cogs-icon";
import { DollarIcon } from "./template-icons/dollar-icon";
import { LightIcon } from "./template-icons/light-icon";
import { PeopleIcon } from "./template-icons/people-icon";
import { SmileyIcon } from "./template-icons/smiley-icon";
import { StarIcon } from "./template-icons/star-icon";

const tilesStyles = css({
    [media.above.md]: {
        padding: `${token("space.800")} 0 0 0`
    },
    "backgroundImage": "linear-gradient(180deg, #deebff, #ffffff)",
    "backgroundPosition": "0 0",
    "backgroundRepeat": "no-repeat",
    "backgroundSize": "100% 100%",
    [media.below.md]: {
        padding: `${token("space.200")} 0 0 0`
    },
    // Note: Existing tech debt, should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".templates-header-content": {
        [media.above.md]: {
            paddingBlockEnd: token("space.800"),
            width: "66.6666667%",
            // Note: Existing tech debt, should be fixed
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                fontSize: "1.5rem",
                marginBlockEnd: "14px",
                paddingBlockStart: token("space.0")
            }
        },

        "margin": "auto",
        // Note: Existing tech debt, should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h2, p, a": {
            webkitFontSmoothing: "antialiased",
            mozOsxFontSmoothing: "grayscale"
        },
        // Note: Existing tech debt, should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "h2": {
            fontSize: "2.25rem",
            lineHeight: 1.3333333333,
            color: "#253858",
            marginBlockEnd: "24px",
            fontWeight: 500,
            paddingBlockStart: token("space.200")
        },
        // Note: Existing tech debt, should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        "p": {
            color: "#42526e",
            marginBlockEnd: "24px",
            fontWeight: "normal",
            fontSize: "1.25rem",
            lineHeight: 1.4,
            letterSpacing: "-0.5px"
        },
        [media.below.md]: {
            paddingBlockEnd: token("space.0"),
            width: "100%",
            // Note: Existing tech debt, should be fixed
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            h2: {
                fontSize: "1.5rem",
                marginBlockEnd: "14px",
                paddingBlockStart: token("space.0")
            }
        }
    },
    // Note: Existing tech debt, should be fixed
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".templates-show-more-content": {
        padding: `${token("space.400")} 0`,
        // Note: Existing tech debt, should be fixed
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        a: {
            "margin": "0px",
            "padding": "4px 10px",
            "fontWeight": 400,
            "height": "unset",
            "&:hover, &:focus": {
                textShadow: "none"
            }
        }
    }
});

const templateCardContainer = css({
    display: "grid",
    margin: "auto",
    maxWidth: "1200px",
    gap: token("space.400"),
    [media.above.sm]: {
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)"
    },
    [media.below.sm]: {
        gridTemplateColumns: "minmax(0, 1fr)"
    }
});

const headerRow = css({
    display: "flex",
    alignItems: "center",
    maxWidth: "1200px",
    margin: "auto",
    [media.below.md]: {
        padding: `${token("space.200")} 0 ${token("space.400")}`
    }
});

const getFeaturedTemplates = (
    intl: IntlShape,
    learnMoreLabel: string
): TemplateCardProps[] => [
    {
        icon: <CogsIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.itsm.title",
            defaultMessage: "ITSM"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.itsm.name",
            defaultMessage: "IT service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.itsm.description",
            defaultMessage:
                "Manage requests, incidents, and changes to deliver great service, fast."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.itsm.try",
            defaultMessage: "Try the ITSM template"
        }),
        templateId:
            "com.atlassian.servicedesk:simplified-it-service-management-basic",
        learnMoreLink: "/software/jira/templates/it-service-management",
        learnMoreLabel,
        barColor: "#998dd9"
    },
    {
        icon: <SmileyIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.customer-service.title",
            defaultMessage: "Customer service"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.customer-service.name",
            defaultMessage: "Customer service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.customer-service.description",
            defaultMessage:
                "Deliver great service experiences to external customers, fast."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.customer-service.try",
            defaultMessage: "Try the customer service template"
        }),
        templateId: "com.atlassian.servicedesk:next-gen-external-service-desk",
        learnMoreLink: "/software/jira/templates/customer-service-management",
        learnMoreLabel,
        barColor: "#4c9aff"
    },
    {
        icon: <PeopleIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.hr.title",
            defaultMessage: "Human resources"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.hr.name",
            defaultMessage: "HR service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.hr.description",
            defaultMessage:
                "Manage onboarding and offboarding and respond to staff requests."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.hr.try",
            defaultMessage: "Try the HR template"
        }),
        templateId: "com.atlassian.servicedesk:simplified-hr-service-desk",
        learnMoreLink: "/software/jira/templates/hr-service-management",
        learnMoreLabel,
        barColor: "#0065ff"
    }
];

const getMoreTemplates = (
    intl: IntlShape,
    learnMoreLabel: string
): TemplateCardProps[] => [
    {
        icon: <LightIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.facilities.title",
            defaultMessage: "Facilities"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.facilities.name",
            defaultMessage: "Facilities service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.facilities.description",
            defaultMessage:
                "Manage requests for maintenance, moving, and event planning."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.facilities.try",
            defaultMessage: "Try the facilities template"
        }),
        templateId:
            "com.atlassian.servicedesk:simplified-facilities-service-desk",
        learnMoreLink: "/software/jira/templates/facilities-service-management",
        learnMoreLabel,
        barColor: "#6554c0"
    },
    {
        icon: <BriefCaseIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.legal.title",
            defaultMessage: "Legal"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.legal.name",
            defaultMessage: "Legal service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.legal.description",
            defaultMessage: "Create, manage, and track your contracts."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.legal.try",
            defaultMessage: "Try the legal template"
        }),
        templateId: "com.atlassian.servicedesk:simplified-legal-service-desk",
        learnMoreLink: "/software/jira/templates/legal-service-management",
        learnMoreLabel,
        barColor: "#8777d9"
    },
    {
        icon: <DollarIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.finance.title",
            defaultMessage: "Finance"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.finance.name",
            defaultMessage: "Finance service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.finance.description",
            defaultMessage: "Manage budget, spend, and other finance requests."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.finance.try",
            defaultMessage: "Try the finance template"
        }),
        templateId: "com.atlassian.servicedesk:simplified-finance-service-desk",
        learnMoreLink: "/software/jira/templates/finance-service-management",
        learnMoreLabel,
        barColor: "#0065ff"
    },
    {
        icon: <BullHornIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.marketing.title",
            defaultMessage: "Marketing"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.marketing.name",
            defaultMessage: "Marketing service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.marketing.description",
            defaultMessage: "Manage and track all your marketing requests."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.marketing.try",
            defaultMessage: "Try the marketing template"
        }),
        templateId:
            "com.atlassian.servicedesk:simplified-marketing-service-desk",
        learnMoreLink: "/software/jira/templates/marketing-service-management",
        learnMoreLabel,
        barColor: "#4c9aff"
    },
    {
        icon: <StarIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.design.title",
            defaultMessage: "Design"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.design.name",
            defaultMessage: "Design service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.design.description",
            defaultMessage:
                "Easily manage and track all kinds of design requests."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.design.try",
            defaultMessage: "Try the design template"
        }),
        templateId: "com.atlassian.servicedesk:simplified-design-service-desk",
        learnMoreLink: "/software/jira/templates/design-service-management",
        learnMoreLabel,
        barColor: "#6554c0"
    },
    {
        icon: <BagIcon />,
        title: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.sales.title",
            defaultMessage: "Sales"
        }),
        name: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.sales.name",
            defaultMessage: "Sales service management"
        }),
        description: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.sales.description",
            defaultMessage: "Manage, track, and report on sales requests."
        }),
        try: intl.formatMessage({
            id: "JiraServiceManagementPageTemplate.templates.sales.try",
            defaultMessage: "Try the sales template"
        }),
        templateId: "com.atlassian.servicedesk:simplified-sales-service-desk",
        learnMoreLink: "/software/jira/templates/sales-service-management",
        learnMoreLabel,
        barColor: "#4c9aff"
    }
];

const TilesSection = () => {
    const intl = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleShowMore = () => {
        setIsExpanded(!isExpanded);
    };
    const showMore = intl.formatMessage({
        id: "JiraServiceManagementPageTemplate.templatesSection.showMore",
        defaultMessage: "Show more"
    });
    const showLess = intl.formatMessage({
        id: "JiraServiceManagementPageTemplate.templatesSection.showLess",
        defaultMessage: "Show less"
    });
    const learnMore = intl.formatMessage({
        id: "JiraServiceManagementPageTemplate.templatesSection.learnMore",
        defaultMessage: "Learn more"
    });

    return (
        <section css={tilesStyles}>
            <Row>
                <div css={headerRow}>
                    <ContentBlock
                        className="templates-header-content"
                        headerText={intl.formatMessage({
                            id: "JiraServiceManagementPageTemplate.templatesSection.header",
                            defaultMessage: "Get started quickly with templates"
                        })}
                        bodyText={intl.formatMessage({
                            id: "JiraServiceManagementPageTemplate.templatesSection.body",
                            defaultMessage:
                                "With pre-configured request types, workflows and other out-of-the-box capabilities, templates make it easy for any team to get started quickly with a service experience that fits the way they like to work."
                        })}
                        headerType="h2"
                        isCentered
                        orientation="vertical"
                    />
                </div>
            </Row>
            <Row>
                <div css={templateCardContainer}>
                    {getFeaturedTemplates(intl, learnMore).map((template) => (
                        <TemplateCard key={template.title} {...template} />
                    ))}
                </div>
            </Row>
            <Row>
                <ContentBlock
                    className="templates-show-more-content"
                    headerText=""
                    bodyText=""
                    headerType="h2"
                    isCentered
                    orientation="vertical"
                    ctaProps={{
                        primaryLinkProps: {
                            text: isExpanded ? showLess : showMore,
                            href: "",
                            type: "anchor",
                            onClick(e) {
                                e.preventDefault();
                                toggleShowMore();
                            }
                        }
                    }}
                />
            </Row>

            {isExpanded && (
                <Row>
                    <div css={templateCardContainer}>
                        {getMoreTemplates(intl, learnMore).map((template) => (
                            <TemplateCard key={template.title} {...template} />
                        ))}
                    </div>
                </Row>
            )}
        </section>
    );
};

export default TilesSection;
